import React, { useState, useEffect } from 'react';
import { 
  BarChart3, TrendingUp, Clock, AlertTriangle, CheckCircle, 
  Download, FileSpreadsheet, FileText, Loader2, RefreshCw,
  PieChart, Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { adminApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const CAN_SEE_BY_MANAGER = new Set(['company_admin', 'compliance_officer', 'super_admin']);

// Simple bar chart component
const SimpleBarChart = ({ data, labelKey, valueKey, color = '#0F172A' }) => {
  const maxValue = Math.max(...data.map(d => d[valueKey]));
  
  return (
    <div className="space-y-2">
      {data.map((item, idx) => (
        <div key={idx} className="flex items-center gap-3">
          <span className="text-sm text-slate-600 w-32 truncate">{item[labelKey]}</span>
          <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${maxValue > 0 ? (item[valueKey] / maxValue) * 100 : 0}%`,
                backgroundColor: color 
              }}
            />
          </div>
          <span className="text-sm font-medium w-10 text-right">{item[valueKey]}</span>
        </div>
      ))}
    </div>
  );
};

// Trend line component
const TrendLine = ({ data }) => {
  const maxValue = Math.max(...data.map(d => d.count), 1);
  const points = data.map((d, i) => ({
    x: (i / (data.length - 1)) * 100,
    y: 100 - (d.count / maxValue) * 80
  }));
  
  const pathData = points.map((p, i) => 
    `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
  ).join(' ');
  
  return (
    <div className="relative h-40">
      <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map(y => (
          <line key={y} x1="0" y1={y} x2="100" y2={y} stroke="#e2e8f0" strokeWidth="0.5" />
        ))}
        
        {/* Trend line */}
        <path d={pathData} fill="none" stroke="#0F172A" strokeWidth="2" />
        
        {/* Points */}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="2" fill="#0F172A" />
        ))}
      </svg>
      
      {/* Labels */}
      <div className="flex justify-between mt-2">
        {data.map((d, i) => (
          <span key={i} className="text-xs text-slate-500">{d.label}</span>
        ))}
      </div>
    </div>
  );
};

const AnalyticsPage = () => {
  const { user } = useAuth();
  const canSeeByManager = CAN_SEE_BY_MANAGER.has(user?.role);
  const [analytics, setAnalytics] = useState(null);
  const [byManager, setByManager] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const requests = [adminApi.getAnalytics()];
      if (canSeeByManager) requests.push(adminApi.getAnalyticsByManager());
      const results = await Promise.all(requests);
      setAnalytics(results[0].data);
      if (canSeeByManager) setByManager(results[1].data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const response = await adminApi.exportReports(format);
      
      // Create download link
      const blob = new Blob([response.data], {
        type: format === 'excel' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv'
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Denuncias_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Erro ao carregar analytics</p>
        <Button onClick={fetchAnalytics} className="mt-4">Tentar novamente</Button>
      </div>
    );
  }

  // Prepare data for charts
  const statusData = Object.entries(analytics.by_status || {}).map(([label, count]) => ({
    label,
    count
  })).sort((a, b) => b.count - a.count);

  const categoryData = Object.entries(analytics.by_category || {}).map(([label, count]) => ({
    label,
    count
  })).sort((a, b) => b.count - a.count).slice(0, 8);

  const severityData = Object.entries(analytics.by_severity || {}).map(([label, count]) => ({
    label,
    count
  }));

  const severityColors = {
    'BAIXO': '#22c55e',
    'MEDIO': '#eab308',
    'ALTO': '#f97316',
    'CRITICO': '#ef4444'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Relatorios e Analytics
          </h1>
          <p className="text-slate-500 mt-1">Metricas e estatisticas do canal de denuncias</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchAnalytics}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleExport('csv')}
            disabled={exporting === 'csv'}
            data-testid="export-csv-btn"
          >
            {exporting === 'csv' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            CSV
          </Button>
          <Button 
            size="sm" 
            onClick={() => handleExport('excel')}
            disabled={exporting === 'excel'}
            data-testid="export-excel-btn"
          >
            {exporting === 'excel' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4 mr-2" />
            )}
            Excel
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Denuncias</p>
                <p className="text-3xl font-bold text-slate-900">{analytics.total_reports}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Taxa Resolucao</p>
                <p className="text-3xl font-bold text-emerald-600">{analytics.resolution_rate}%</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Tempo Medio</p>
                <p className="text-3xl font-bold text-slate-900">
                  {analytics.avg_resolution_days || 'N/A'}
                  <span className="text-sm font-normal text-slate-400"> dias</span>
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Em Atraso</p>
                <p className="text-3xl font-bold text-red-600">{analytics.overdue_count}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue="trend" className="space-y-4">
        <TabsList>
          <TabsTrigger value="trend" data-testid="tab-trend">
            <TrendingUp className="w-4 h-4 mr-2" />
            Tendencia
          </TabsTrigger>
          <TabsTrigger value="status" data-testid="tab-status">
            <PieChart className="w-4 h-4 mr-2" />
            Por Estado
          </TabsTrigger>
          <TabsTrigger value="category" data-testid="tab-category">
            <BarChart3 className="w-4 h-4 mr-2" />
            Por Categoria
          </TabsTrigger>
          <TabsTrigger value="severity" data-testid="tab-severity">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Por Gravidade
          </TabsTrigger>
        </TabsList>

        <TabsContent value="trend">
          <Card>
            <CardHeader>
              <CardTitle>Evolucao Mensal</CardTitle>
              <CardDescription>Numero de denuncias nos ultimos 6 meses</CardDescription>
            </CardHeader>
            <CardContent>
              {analytics.trend_data?.length > 0 ? (
                <TrendLine data={analytics.trend_data} />
              ) : (
                <p className="text-center text-slate-400 py-8">Sem dados suficientes</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="status">
          <Card>
            <CardHeader>
              <CardTitle>Distribuicao por Estado</CardTitle>
              <CardDescription>Estado atual das denuncias</CardDescription>
            </CardHeader>
            <CardContent>
              {statusData.length > 0 ? (
                <SimpleBarChart data={statusData} labelKey="label" valueKey="count" color="#0F172A" />
              ) : (
                <p className="text-center text-slate-400 py-8">Sem dados</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="category">
          <Card>
            <CardHeader>
              <CardTitle>Distribuicao por Categoria</CardTitle>
              <CardDescription>Top categorias mais reportadas</CardDescription>
            </CardHeader>
            <CardContent>
              {categoryData.length > 0 ? (
                <SimpleBarChart data={categoryData} labelKey="label" valueKey="count" color="#3b82f6" />
              ) : (
                <p className="text-center text-slate-400 py-8">Sem dados</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="severity">
          <Card>
            <CardHeader>
              <CardTitle>Distribuicao por Gravidade</CardTitle>
              <CardDescription>Classificacao de gravidade das denuncias</CardDescription>
            </CardHeader>
            <CardContent>
              {severityData.length > 0 ? (
                <div className="space-y-3">
                  {severityData.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <Badge 
                        variant="outline" 
                        className="w-20 justify-center"
                        style={{ 
                          backgroundColor: `${severityColors[item.label]}20`,
                          borderColor: severityColors[item.label],
                          color: severityColors[item.label]
                        }}
                      >
                        {item.label}
                      </Badge>
                      <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${analytics.total_reports > 0 ? (item.count / analytics.total_reports) * 100 : 0}%`,
                            backgroundColor: severityColors[item.label]
                          }}
                        />
                      </div>
                      <span className="text-sm font-medium w-10 text-right">{item.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-400 py-8">Sem dados de gravidade</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Resolvidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600">{analytics.resolved_count || 0}</div>
            <p className="text-sm text-slate-500">denuncias concluidas ou arquivadas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pendentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-600">{analytics.pending_count || 0}</div>
            <p className="text-sm text-slate-500">em analise ou investigacao</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Este Mes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">
              {analytics.trend_data?.slice(-1)[0]?.count || 0}
            </div>
            <p className="text-sm text-slate-500">novas denuncias</p>
          </CardContent>
        </Card>
      </div>

      {/* Performance por Gestor (only for compliance/admin) */}
      {canSeeByManager && byManager && byManager.managers && byManager.managers.length > 0 && (
        <Card data-testid="analytics-by-manager-card">
          <CardHeader>
            <CardTitle>Performance por Gestor</CardTitle>
            <CardDescription>
              Distribuição de casos, taxa de resolução e tempo médio por utilizador atribuído.
              Total de casos atribuídos: <strong>{byManager.total_assigned}</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200">
                  <tr className="text-slate-500 text-left">
                    <th className="py-2 pr-3 font-medium">Gestor</th>
                    <th className="py-2 px-3 font-medium">Papel</th>
                    <th className="py-2 px-3 font-medium text-right">Total</th>
                    <th className="py-2 px-3 font-medium text-right">Abertos</th>
                    <th className="py-2 px-3 font-medium text-right">Fechados</th>
                    <th className="py-2 px-3 font-medium text-right">Feedback em atraso</th>
                    <th className="py-2 px-3 font-medium text-right">Tempo médio (dias)</th>
                    <th className="py-2 pl-3 font-medium text-right">Taxa de resolução</th>
                  </tr>
                </thead>
                <tbody>
                  {byManager.managers.map((m) => (
                    <tr
                      key={m.user_id}
                      className="border-b border-slate-100 last:border-0"
                      data-testid={`by-manager-row-${m.user_id}`}
                    >
                      <td className="py-2 pr-3">
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-900">{m.name}</span>
                          {!m.is_active && <span className="text-xs text-slate-400">Inativo</span>}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-slate-600">{(m.role || '').replace('_', ' ')}</td>
                      <td className="py-2 px-3 text-right font-medium text-slate-900">{m.total}</td>
                      <td className="py-2 px-3 text-right text-slate-700">{m.open}</td>
                      <td className="py-2 px-3 text-right text-slate-700">{m.closed}</td>
                      <td className="py-2 px-3 text-right">
                        {m.overdue_feedback > 0 ? (
                          <Badge className="bg-red-100 text-red-700 border-red-200" variant="outline">
                            {m.overdue_feedback}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right text-slate-700">
                        {m.avg_resolution_days !== null ? m.avg_resolution_days : '-'}
                      </td>
                      <td className="py-2 pl-3 text-right font-medium text-slate-900">
                        {m.resolution_rate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsPage;
